import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { RefreshCw, Activity, CheckCircle2, XCircle, AlertCircle, Wifi, WifiOff } from "lucide-react";

function relTime(seconds) {
  if (seconds < 0) return "?";
  if (seconds < 60) return `prije ${seconds}s`;
  if (seconds < 3600) return `prije ${Math.floor(seconds / 60)}min`;
  if (seconds < 86400) return `prije ${Math.floor(seconds / 3600)}h`;
  return `prije ${Math.floor(seconds / 86400)}d`;
}

function fmtUptime(seconds) {
  if (!seconds || seconds < 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function StatusPill({ ok, label, testid }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  const cls = ok
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : "bg-red-100 text-red-800 border-red-300";
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] border ${cls}`} data-testid={testid}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

export default function PiHealthCard() {
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/rfid-pi/health");
      setReaders(r.data.readers || []);
      setCheckedAt(r.data.checked_at);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri dohvatu statusa");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(load, 15000); // auto-refresh svakih 15s
    return () => clearInterval(iv);
  }, [autoRefresh, load]);

  const anyStale = useMemo(
    () => readers.some(r => r.stale_seconds > 180),
    [readers]
  );

  return (
    <Card data-testid="pi-health-card" className="border-cyan-400/30 bg-cyan-50/30 dark:bg-cyan-950/10">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-700 dark:text-cyan-300" />
              Provjera Pi-ja uživo (heartbeat)
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Svaki RFID Pi šalje self-test na server svakih 60 sekundi. Ovdje odmah vidiš status čitača bez SSH-anja.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs cursor-pointer" title="Auto-osvježi svakih 15s">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="pi-health-auto-refresh"
              />
              Auto (15s)
            </label>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="pi-health-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Osvježi
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {readers.length === 0 && (
          <div className="rounded border bg-background/60 p-4 text-sm text-muted-foreground italic">
            Nema registriranih čitača — čitač se pojavi ovdje nakon prvog heartbeata (do 60s nakon paljenja).
            <br />
            Ako Pi ne šalje heartbeat: pokreni <code className="text-xs">kif-doctor</code> na Pi-ju i provjeri točnost tokena i URL-a.
          </div>
        )}

        {readers.length > 0 && (
          <>
            {anyStale && (
              <div className="rounded border border-amber-300 bg-amber-50/70 p-2 text-xs text-amber-900 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                Jedan ili više čitača nije javio više od 3 minute — provjeri strujno napajanje i mrežu.
              </div>
            )}

            <div className="space-y-3">
              {readers.map((r) => {
                const stale = r.stale_seconds > 180;
                return (
                  <div
                    key={r.reader_id}
                    className={`rounded border p-3 space-y-2 ${
                      stale ? "border-red-300 bg-red-50/50" : "border-emerald-300 bg-emerald-50/40"
                    }`}
                    data-testid={`pi-reader-${r.reader_id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="font-medium text-sm flex items-center gap-2">
                          {stale ? <WifiOff className="w-4 h-4 text-red-600" /> : <Wifi className="w-4 h-4 text-emerald-600" />}
                          <span>{r.reader_id}</span>
                          <Badge variant="outline" className="text-[10px]">{r.hw || "?"}</Badge>
                          {r.version && <Badge variant="secondary" className="text-[10px]">{r.version}</Badge>}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          IP: {r.ip_address || "?"} · uptime: {fmtUptime(r.uptime_s)} · api: {r.api_url || "?"}
                        </div>
                      </div>
                      <Badge className={stale ? "bg-red-600" : "bg-emerald-600"} data-testid={`pi-reader-${r.reader_id}-freshness`}>
                        {relTime(r.stale_seconds)}
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <StatusPill ok={r.spi_ok} label="SPI kernel" testid={`pi-${r.reader_id}-spi`} />
                      <StatusPill ok={r.spidev_ok} label="/dev/spidev0.0" testid={`pi-${r.reader_id}-spidev`} />
                      <StatusPill ok={r.mfrc522_ok} label="MFRC522 lib" testid={`pi-${r.reader_id}-mfrc522`} />
                      <StatusPill ok={r.backend_ok} label="Backend GET" testid={`pi-${r.reader_id}-backend`} />
                      <StatusPill ok={r.token_ok} label="Token auth" testid={`pi-${r.reader_id}-token`} />
                    </div>

                    {r.last_scan_ts && (
                      <div className="text-[11px] text-muted-foreground">
                        Zadnji scan: <b>{r.last_scan_uid}</b> ({r.last_scan_kind}) · {r.last_scan_ts.split("T")[1]?.split(".")[0]?.substring(0, 8)}
                      </div>
                    )}

                    {Array.isArray(r.recent_errors) && r.recent_errors.length > 0 && (
                      <details className="text-[11px]">
                        <summary className="cursor-pointer text-red-700">
                          {r.recent_errors.length} nedavne greške
                        </summary>
                        <ul className="mt-1 space-y-0.5 pl-3 text-red-800">
                          {r.recent_errors.map((e) => <li key={`err-${e}`} className="font-mono">{e}</li>)}
                        </ul>
                      </details>
                    )}

                    {r.pkg_versions && (
                      <details className="text-[11px]">
                        <summary className="cursor-pointer text-muted-foreground">
                          Python paketi
                        </summary>
                        <div className="grid grid-cols-2 gap-1 pl-3 pt-1 font-mono text-[10px]">
                          {Object.entries(r.pkg_versions).map(([k, v]) => (
                            <div key={k}>
                              <span className="text-muted-foreground">{k}:</span>{" "}
                              <span className={v === "MISSING" ? "text-red-700" : "text-emerald-700"}>{v}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>

            {checkedAt && (
              <div className="text-[11px] text-muted-foreground text-right">
                Zadnja provjera: {new Date(checkedAt).toLocaleTimeString("hr-HR")}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
